import type {
	ChampionByPromoterData,
	CombatregRanking,
	CombatregRankings,
	UfcRankings,
	UfcRanking,
	BoutResponse,
	BoutStatsResponse,
	DetailedEventResponse
} from './types';

import type {
	ChampionInfo,
	PromotionInfo,
	PromotionRankings,
	BoutInfo,
	BoutStats,
	EventWithBouts
} from '../../types';

import {
	mapPromotionWithChampions,
	mapCombatregRankings,
	mapUfcRankings,
	mapBout,
	mapBoutStats,
	mapEvent
} from './mappers';

const baseUrl = import.meta.env.VITE_API_BASE_URL;
const apiKey = import.meta.env.VITE_API_KEY;

async function fetchApi<K, T>(path: string, mapper: (data: K) => T): Promise<T> {
	const url = `${baseUrl}${path}`;

	const response = await fetch(url, {
		headers: {
			'X-API-Key': apiKey
		}
	});

	const data: K = await response.json();

	if (response.ok) {
		return mapper(data);
	} else {
		return Promise.reject('Failed to fetch');
	}
}

export async function fetchChampions(promotion: PromotionInfo): Promise<ChampionInfo[]> {
	return fetchApi<ChampionByPromoterData, ChampionInfo[]>(
		`/champions/${promotion.id}`,
		mapPromotionWithChampions
	);
}

export async function fetchRanking(promotion: PromotionInfo): Promise<PromotionRankings> {
	if (promotion.slug === 'ufc') {
		const rankings = await fetchUfcRankings();
		return mapUfcRankings(rankings);
	} else {
		const rankings = await fetchCombatregRankings(promotion.slug);
		return mapCombatregRankings(promotion, rankings);
	}
}

async function fetchUfcRankings(): Promise<UfcRanking> {
	return fetchApi<UfcRankings, UfcRanking>('/api/rankings/ufc', (data) => data.UFCRankings);
}

async function fetchCombatregRankings(slug: 'bkfc' | 'pfl'): Promise<CombatregRanking> {
	return fetchApi<CombatregRankings, CombatregRanking>(
		`/api/rankings/${slug}`,
		(data) => data.rankings
	);
}

export async function fetchBout(id: string | null): Promise<BoutInfo> {
	return fetchApi<BoutResponse, BoutInfo>(`/bouts/${id}`, mapBout);
}

export async function fetchBoutStats(id: number): Promise<BoutStats> {
	return fetchApi<BoutStatsResponse, BoutStats>(
		`/api/bkfc?type=json&modifier=bout-stats&id=${id}`,
		mapBoutStats
	);
}

export async function fetchEvent(id: string | null): Promise<EventWithBouts> {
	if (id === null) {
		return Promise.reject('Invalid event ID');
	}
	return fetchApi<DetailedEventResponse, EventWithBouts>(`/events/${id}/detailed`, mapEvent);
}
